import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import componentsAPI from '../../api/components';
import './pages.css';

const ComponentPicker = ({ onSelect, onClose }) => {
  const [components, setComponents] = useState([]);
  const [filteredComponents, setFilteredComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchComponents();
  }, []);

  useEffect(() => {
    filterComponents();
  }, [searchTerm, selectedCategory, components]);

  const fetchComponents = async () => {
    try {
      setLoading(true);
      const response = await componentsAPI.getAllComponents();

      // API returns { status: 200, data: [...components...] }
      if (response?.data?.status === 200 || response?.data?.success) {
        const componentsData = response.data.data || [];
        console.log('Components loaded:', componentsData.length);
        setComponents(componentsData);
        setFilteredComponents(componentsData);
      }
    } catch (error) {
      console.error('Error fetching components:', error);
      toast.error('Failed to load components');
    } finally {
      setLoading(false);
    }
  };

  const filterComponents = () => {
    let filtered = [...components];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((comp) => comp.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (comp) =>
          comp.displayName.toLowerCase().includes(term) ||
          comp.componentKey.toLowerCase().includes(term) ||
          comp.description.toLowerCase().includes(term) ||
          comp.tags.some((tag) => tag.toLowerCase().includes(term))
      );
    }

    setFilteredComponents(filtered);
  };

  const handleSelect = (componentKey) => {
    onSelect(componentKey);
  };

  // Get unique categories
  const categories = ['all', ...new Set(components.map((c) => c.category))];

  const getCategoryIcon = (category) => {
    const icons = {
      hero: 'fa-star',
      content: 'fa-align-left',
      social: 'fa-users',
      stats: 'fa-chart-bar',
      media: 'fa-image',
      forms: 'fa-wpforms',
      layout: 'fa-th',
      all: 'fa-th-large',
    };
    return icons[category] || 'fa-cube';
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Section</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="component-picker-container">
          {/* Search */}
          <div className="component-search">
            <input
              type="text"
              className="form-control"
              placeholder="Search components..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Category Filter */}
          <div className="component-categories">
            {categories.map((category) => (
              <button
                key={category}
                className={`btn btn-sm ${
                  selectedCategory === category ? 'btn-primary' : 'btn-outline-primary'
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                <i className={`fas ${getCategoryIcon(category)} me-2`}></i>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>

          {/* Loading */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading components...</p>
            </div>
          ) : filteredComponents.length === 0 ? (
            <div className="text-center py-5">
              <i className="fa fa-search fa-3x text-muted mb-3"></i>
              <p className="text-muted">No components found</p>
            </div>
          ) : (
            /* Components Grid */
            <div className="component-grid">
              {filteredComponents.map((component) => (
                <div
                  key={component.componentKey}
                  className="component-card"
                  onClick={() => handleSelect(component.componentKey)}
                >
                  <div className="component-card-header">
                    <h6 className="component-card-title">{component.displayName}</h6>
                    {component.isDefault && (
                      <span className="badge bg-success">Default</span>
                    )}
                  </div>

                  <p className="component-card-description">{component.description}</p>

                  <div className="component-card-tags">
                    <span className="badge bg-primary">{component.category}</span>
                    {component.version && (
                      <span className="badge bg-secondary">v{component.version}</span>
                    )}
                    {component.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="badge bg-light text-dark">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-3">
                    <small className="text-muted">
                      <i className="fa fa-code me-1"></i>
                      {component.componentKey}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ComponentPicker;
